import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useContext,
} from "react";
import { action } from "mobx";
import { observer } from "mobx-react";
import { DateTime } from "luxon";
import _ from "lodash";

import { EventsList } from "../../MonitorEvents";
import { HistoryContext } from "../../../../routes";
import { MonitorContext } from "../../Monitor";

function blocker(e) {
  e.stopImmediatePropagation();
}

const StickyList = observer(
  forwardRef(function StickyList({ eventsStore, parent }, ref) {
    const monitorStore = useContext(MonitorContext).store;
    const history = useContext(HistoryContext);
    const stickyList = ref;

    const scrollJack = useCallback(
      (arg) => {
        const el = stickyList.current;
        if (!el) return;
        if (typeof arg === "number") {
          if (arg !== el.scrollTop) {
            el.addEventListener("scroll", blocker, {
              capture: true,
              once: true,
            });
            el.scrollTop = arg;
            el.removeEventListener("scroll", blocker, {
              capture: true,
              once: true,
            });
          }
          return Promise.resolve();
        }
        if (typeof arg === "function") {
          return new Promise((resolve) => {
            el.addEventListener("scroll", blocker, { capture: true });
            el.addEventListener(
              "scrollEnd",
              () => {
                console.log("scrollEnd");
                cleanup();
              },
              { once: true },
            );
            const timer = setTimeout(cleanup, 1000, [true]);
            const result = arg();
            function cleanup(timeout) {
              if (timeout) console.log("timeout");
              clearTimeout(timer);
              el.removeEventListener("scroll", blocker, { capture: true });
              el.removeEventListener("scrollEnd", cleanup, { once: true });
              resolve(result);
            }
          });
        }
      },
      [stickyList],
    );

    const scrollToEvent = useCallback(
      (pmEvent, options) => {
        if (!pmEvent) return;
        const selected = stickyList.current?.querySelector(
          `[data-event-time="${pmEvent.eventTime}"]`,
        );
        if (!selected) return;
        requestAnimationFrame(() => {
          const list = stickyList.current;
          const headerHeight = selected
            .closest(".segment")
            .querySelector(".header").clientHeight;
          const top = selected.offsetTop - headerHeight;
          if (list.scrollTop === top) return;
          scrollJack(() => {
            list.scroll({ ...options, top });
            if (top < 0) eventsStore.liveMode = true;
          });
        });
        return selected;
      },
      [stickyList],
    );

    const scrollToTime = useCallback(
      ({ timestamp, behavior }) => {
        const newTime = DateTime.fromISO(timestamp);
        if (!newTime.isValid)
          return console.error(
            "failed to update EventsSidebar position to time",
            timestamp,
          );
        const prevIndex = eventsStore.eventsObj?.eventData?.findIndex(
          (pmEvent) => pmEvent.dateTime <= newTime,
        );
        eventsStore.liveMode = false;
        if (prevIndex === -1) {
          return stickyList.current.scrollTo({
            top: stickyList.current.scrollHeight,
            behavior,
          });
        }
        if (prevIndex === 0)
          return stickyList.current.scrollTo({ top: 0, behavior });
        let pmEvent = eventsStore.eventsObj?.eventData[prevIndex];
        if (pmEvent?.dateTime.equals(newTime))
          return scrollToEvent(pmEvent, { behavior });
        const nextEvent = eventsStore.eventsObj?.eventData[prevIndex - 1];
        if (nextEvent.dateTime - newTime < newTime - pmEvent.dateTime) {
          return scrollToEvent(nextEvent, { behavior });
        }
        return scrollToEvent(pmEvent, { behavior: behavior });
      },
      [stickyList],
    );

    useLayoutEffect(() => {
      if (eventsStore.loadingEvents || eventsStore.preSelected) return;
      const { routeParams } = monitorStore;
      let { timestamp, historyType } = routeParams;
      if (historyType?.match(/event/) && timestamp) {
        timestamp = decodeURIComponent(timestamp);
        try {
          const dateTime = DateTime.fromISO(timestamp)
            .toUTC()
            .set({ millisecond: 0 });
          if (dateTime.isValid) {
            eventsStore.selectedEvent = eventsStore.eventsObj?.eventData?.find(
              (pmEvent) => pmEvent.dateTime.equals(dateTime),
            );
            if (scrollToEvent(eventsStore.selectedEvent))
              eventsStore.preSelected = true;
          }
        } catch (e) {
          console.error("failed to pre-select event", e);
        }
      }
    }, [eventsStore.loadingEvents]);

    useLayoutEffect(() => {
      // automatically load more to fill up the page or confirm completion.
      const { scrollHeight, clientHeight } = stickyList.current;
      eventsStore.pageFilled = scrollHeight > clientHeight;
      if (
        !eventsStore.loadingEvents &&
        !eventsStore.liveMode &&
        eventsStore.eventsObj?.eventData?.length &&
        !eventsStore.historyComplete &&
        !eventsStore.pageFilled
      ) {
        eventsStore.fetchHistory();
      }
    }, [eventsStore.liveMode, eventsStore.eventsObj?.eventData?.length]);

    useEffect(() => {
      const disposers = [];
      const timeSelectHandler = action(async function (e) {
        eventsStore.liveMode = false;
        await eventsStore.fetchPmEvents({
          reset: true,
          eventTime: e.detail.newTime,
        });
        scrollToTime({
          timestamp: e.detail.newTime,
          behavior: e.detail.behavior,
        });
      });
      parent?.eventTarget?.addEventListener("timeSelect", timeSelectHandler);
      disposers.push(() =>
        parent.eventTarget?.removeEventListener(
          "timeSelect",
          timeSelectHandler,
        ),
      );

      const liveModeStartHandler = action((e) => {
        scrollJack(stickyList.current.scrollTo({ top: 0, behavior: "smooth" }));
        eventsStore.liveMode = true;
        return e;
      });
      parent?.eventTarget?.addEventListener(
        "liveModeStart",
        liveModeStartHandler,
      );
      disposers.push(() =>
        parent?.eventTarget?.removeEventListener(
          "liveModeStart",
          liveModeStartHandler,
        ),
      );
      return () => disposers.forEach((d) => d());
    }, [parent]);
    useEffect(() => {
      if (!stickyList.current) return;
      const listener = ({ detail }) => {
        scrollToTime(detail);
      };
      stickyList.current.addEventListener("timeSet", listener);
    }, [stickyList.current]);

    function selectEvent(pmEvent) {
      if (!pmEvent) return;
      eventsStore.selectedEvent = pmEvent;
      parent.gotoTime(pmEvent.dateTime);
      eventsStore.liveMode = false;
    }

    const loadMore = useCallback(({ up } = {}) => {
      if (eventsStore.pollingEvents) return;
      if (up) {
        const topEvent = eventsStore.eventsObj?.eventData?.[0];
        eventsStore
          .fetchHistory({ later: true })
          ?.then(() => scrollToEvent(topEvent, { behavior: "instant" }));
      } else {
        eventsStore.liveMode = false;
        if (!eventsStore.historyComplete) {
          eventsStore.fetchHistory();
        }
      }
    });

    const onScroll = useCallback((e) => {
      e.scrollDelta = onScroll.lastScrollTop - e.target.scrollTop;
      onScroll.lastScrollTop = e.target.scrollTop;
      handleScroll(e);
    });
    const onWheel = useCallback((e) => {
      if (e.deltaY > 0) {
        e.scrollDelta = -e.deltaY;
        handleScroll(e);
      }
    });
    const handleScroll = useCallback(
      action(
        _.throttle(
          (e) => {
            if (e.scrollDelta < 0) {
              eventsStore.liveMode = false;
            }
          },
          200,
          { leading: true, trailing: true },
        ),
      ),
      [],
    );

    const { liveMode, pollingEvents, eventsObj, historyComplete, pageFilled } =
      eventsStore;
    return (
      <div className="sticky-list" ref={stickyList} {...{ onScroll, onWheel }}>
        {!liveMode && (
          <button
            className="load-more btn"
            onClick={() => loadMore({ up: true })}
            disabled={pollingEvents}
          >
            Load More
          </button>
        )}
        {eventsObj?.eventData?.length === 0 &&
          (eventsStore.filterCount ? (
            <div className="warning">No Results for Search Criteria</div>
          ) : (
            <div className="no-events warning">No Events</div>
          ))}
        <EventsList
          history={history}
          store={eventsStore}
          selectEvent={selectEvent}
          parent={parent}
        />
        {!liveMode && !historyComplete && pageFilled && (
          <button
            className="load-more btn"
            onClick={() => loadMore()}
            disabled={pollingEvents}
          >
            Load More
          </button>
        )}
      </div>
    );
  }),
);

export default StickyList;

export function findListTime(list) {
  return [...list.querySelectorAll(".event")].find((el) => {
    const headerHeight =
      el.closest(".segment")?.querySelector(".header")?.clientHeight || 0;
    return el.offsetTop + el.clientHeight / 2 >= list.scrollTop + headerHeight;
  })?.dataset?.eventTime;
}
